import { MaskType } from '@ds/form/input/text/text-input.types';

export const SECURITY_CHARACTER = '•';
const SEPARATOR_CHARACTER = '-';
const PLACEHOLDER_CHARACTER = '_';

/**
 * Get the masked input value for SSN, EIN, or Account Number
 */
export const getMaskedValue = (
    inputValue: string,
    mask: MaskType,
    numVisibleCharacters: number,
    securityCharacter = SECURITY_CHARACTER,
) => {
    // edge case: if numVisibleCharacters is 0, return only masked characters
    if (numVisibleCharacters === 0) {
        return getMaskedCharacters(inputValue, mask, numVisibleCharacters);
    }

    const maskedCharacters = getMaskedCharacters(inputValue, mask, numVisibleCharacters, securityCharacter);
    const visibleCharacters = getVisibleCharacters(inputValue, numVisibleCharacters);
    return maskedCharacters + visibleCharacters;
};

/**
 * Finds the position of the last character that contains actual data (not a dash `-` or underscore `_`)
 */
const getLastFilledIndex = (inputValue: string) => {
    return inputValue.split('').findLastIndex(char => char !== SEPARATOR_CHARACTER && char !== PLACEHOLDER_CHARACTER);
};

/**
 * Gets the masked characters right before the last visible characters, i.e. '•••-••-' in '•••-••-6789'
 * If numVisibleCharacters is 0, return the entire masked value, i.e. '•••-••-••••'
 */
const getMaskedCharacters = (
    inputValue: string,
    mask: MaskType,
    numVisibleCharacters: number,
    securityCharacter = SECURITY_CHARACTER,
) => {
    const maskedValue = replaceCharacters(inputValue, mask, securityCharacter);
    return numVisibleCharacters === 0 ? maskedValue : maskedValue.slice(0, -numVisibleCharacters);
};

/**
 * Get the last visible characters, i.e. '6789' in '•••-••-6789'
 */
export const getVisibleCharacters = (inputValue: string, numVisibleCharacters: number) => {
    return inputValue.slice(-numVisibleCharacters);
};

/**
 * Count filled positions by removing placeholder characters `_`
 */
const isInputComplete = (inputValue: string, mask: MaskType) => {
    // Account numbers don't have a fixed length, so always consider them complete for masking
    if (mask === MaskType.ACCOUNT_NUMBER) {
        return true;
    }

    const filledCharacterCount = inputValue.replaceAll(PLACEHOLDER_CHARACTER, '').length;
    return filledCharacterCount === mask.length;
};

/**
 * Replace all characters in input value that are not a separator `-` with our security character
 */
const replaceCharacters = (inputValue: string, mask: MaskType, securityCharacter = SECURITY_CHARACTER) => {
    const isComplete = isInputComplete(inputValue, mask);
    const lastFilledIndex = getLastFilledIndex(inputValue);

    return inputValue
        .split('')
        .map((char, index) => {
            if (char === SEPARATOR_CHARACTER) {
                return char; // keep separators as-is
            }
            if (isComplete || (lastFilledIndex !== -1 && index <= lastFilledIndex)) {
                return securityCharacter; // mask filled positions
            }
            return PLACEHOLDER_CHARACTER; // show placeholder for unfilled positions
        })
        .join('');
};

/**
 * Format input value for SSN
 * SSN format: XXX-XX-XXXX
 */
export const formatSSN = (value: string) => {
    let digits = removeNonDigits(value);
    if (digits.length > 5) {
        digits = digits.slice(0, 3) + SEPARATOR_CHARACTER + digits.slice(3, 5) + SEPARATOR_CHARACTER + digits.slice(5, 9);
    } else if (digits.length > 3) {
        digits = digits.slice(0, 3) + SEPARATOR_CHARACTER + digits.slice(3);
    }
    return digits;
};

export const maskSSN = (value: string) => getMaskedValue(formatSSN(value), MaskType.SSN, 4, '*');

/**
 * Format input value for EIN
 * EIN format: XX-XXXXXXX
 */
export const formatEIN = (value: string) => {
    let digits = removeNonDigits(value);
    if (digits.length > 2) {
        digits = digits.slice(0, 2) + SEPARATOR_CHARACTER + digits.slice(2, 9);
    }
    return digits;
};

/**
 * Format input value for Account Number
 * Account numbers are kept as-is (no formatting, no dashes)
 */
export const formatAccountNumber = (value: string) => {
    return value;
};

/**
 * Helper to remove non-digits from input value
 */
const removeNonDigits = (value: string) => {
    return value.replace(/\D/g, '');
};
